import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Eye, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GuestGalleryMasonryGrid } from '@/components/planner/GuestGalleryMasonryGrid';
import { GuestGalleryLightbox } from '@/components/planner/GuestGalleryLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestGalleryUploadPage({ mode = 'upload' }) {
  const { slug } = useParams();
  const isPublicGalleryMode = mode === 'public-gallery';

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState('');
  const [galleryMeta, setGalleryMeta] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const loadMeta = async () => {
      setLoadingMeta(true);
      setMetaError('');
      try {
        if (isPublicGalleryMode) {
          const response = await axios.get(`${API}/weddings/${slug}/gallery`);
          setGalleryMeta(response.data || null);
          setGalleryPhotos(Array.isArray(response.data?.photos) ? response.data.photos : []);
        } else {
          const response = await axios.get(`${API}/guest-gallery/${slug}`);
          setGalleryMeta(response.data || null);
          setGalleryPhotos([]);
        }
      } catch (error) {
        setMetaError(error?.response?.data?.detail || 'Upload page not found');
      } finally {
        setLoadingMeta(false);
      }
    };

    loadMeta();
  }, [slug, isPublicGalleryMode]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const activePhotos = isPublicGalleryMode ? galleryPhotos : uploadedPhotos;
    if (lightboxIndex < activePhotos.length) return;
    setLightboxIndex(null);
  }, [galleryPhotos, uploadedPhotos, lightboxIndex, isPublicGalleryMode]);

  const coupleTitle = useMemo(() => {
    if (!galleryMeta?.couple_names) return isPublicGalleryMode ? 'Public Guest Gallery' : 'Wedding Photo Sharing';
    return isPublicGalleryMode
      ? `${galleryMeta.couple_names} · Public Guest Gallery`
      : `${galleryMeta.couple_names} · Wedding Photo Sharing`;
  }, [galleryMeta, isPublicGalleryMode]);

  const weddingDateText = useMemo(() => {
    if (!galleryMeta?.wedding_date) return 'Wedding day';
    const parsed = new Date(galleryMeta.wedding_date);
    if (Number.isNaN(parsed.getTime())) return galleryMeta.wedding_date;
    return parsed.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [galleryMeta]);

  const onFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setUploadMessage('');
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFiles.length) {
      setUploadMessage('Please choose at least one photo.');
      return;
    }

    setUploading(true);
    setUploadMessage('');

    if (galleryMeta?.max_photo_count && galleryMeta?.photo_count >= galleryMeta.max_photo_count) {
      setUploadMessage(`This gallery is full. Maximum ${galleryMeta.max_photo_count} photos allowed.`);
      setUploading(false);
      return;
    }

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));

      const response = await axios.post(`${API}/guest-gallery/${slug}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newPhotos = Array.isArray(response.data?.photos) ? response.data.photos : [];
      setUploadedPhotos((prev) => [...newPhotos, ...prev]);
      setGalleryMeta((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          photo_count: Number(prev.photo_count || 0) + newPhotos.length,
        };
      });
      setSelectedFiles([]);
      setFileInputKey((prev) => prev + 1);
      setUploadMessage(`Thanks! Uploaded ${newPhotos.length} photo${newPhotos.length === 1 ? '' : 's'}.`);
    } catch (error) {
      setUploadMessage(error?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const activeLightboxPhotos = isPublicGalleryMode ? galleryPhotos : uploadedPhotos;
  const maxPhotoLabel = galleryMeta?.max_photo_count ? `${galleryMeta.max_photo_count} photos max` : 'Photo limit applies';

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#faf7f2_0%,#f6f1ea_100%)] px-4 py-8 sm:px-6" data-testid="guest-gallery-upload-page">
      <div className={`mx-auto ${isPublicGalleryMode ? 'max-w-6xl' : 'max-w-3xl'}`}>
        {loadingMeta ? (
          <div className="py-10 text-center" data-testid="guest-gallery-upload-loading">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400 mx-auto" />
          </div>
        ) : (
          <>
            <header className="mb-8 text-center" data-testid="guest-gallery-upload-wedding-header">
              <p className="text-xs uppercase tracking-[0.22em] text-[#5C0120]/80 font-semibold" data-testid="guest-gallery-upload-kicker">
                {isPublicGalleryMode ? 'Public Guest Gallery' : 'Wedding Guest Photos'}
              </p>
              <h1 className="mt-3 text-4xl sm:text-5xl font-serif text-stone-900" data-testid="guest-gallery-upload-couple-names">
                {galleryMeta?.couple_names || 'Wedding Celebration'}
              </h1>
              <p className="mt-2 text-sm text-stone-600" data-testid="guest-gallery-upload-wedding-date">
                {weddingDateText}
              </p>
            </header>

            <div className="rounded-sm border border-stone-200 bg-white p-6 md:p-8 shadow-sm" data-testid="guest-gallery-upload-card">
              {metaError ? (
                <div className="py-8 text-center" data-testid="guest-gallery-upload-error">
                  <h2 className="text-2xl font-semibold text-stone-900" data-testid="guest-gallery-upload-error-title">
                    {isPublicGalleryMode ? 'Public gallery unavailable' : 'Upload page unavailable'}
                  </h2>
                  <p className="text-stone-600 mt-2">{metaError}</p>
                </div>
              ) : (
                <>
                  {isPublicGalleryMode ? (
                    <section data-testid="wedding-public-guest-gallery-page">
                      <h2 className="text-3xl font-semibold text-stone-900" data-testid="wedding-public-guest-gallery-title">
                        Shared Wedding Moments
                      </h2>
                      <p className="mt-3 text-sm text-stone-600" data-testid="wedding-public-guest-gallery-subtitle">
                        {coupleTitle}
                      </p>
                      <p className="mt-2 text-sm text-stone-600" data-testid="wedding-public-guest-gallery-count">
                        {galleryPhotos.length} photo{galleryPhotos.length === 1 ? '' : 's'} shared by guests
                      </p>

                      <div className="mt-6" data-testid="wedding-public-guest-gallery-grid-wrap">
                        <GuestGalleryMasonryGrid
                          photos={galleryPhotos}
                          onPhotoClick={setLightboxIndex}
                          emptyTitle="No guest photos have been shared yet"
                          emptySubtitle="When guests upload photos, they will appear here automatically."
                          testIdPrefix="wedding-public-guest-gallery-masonry"
                        />
                      </div>

                      <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-testid="wedding-public-guest-gallery-actions">
                        <Link to={`/weddings/${slug}`} data-testid="wedding-public-guest-gallery-back-link">
                          <Button type="button" variant="outline" className="border-stone-300" data-testid="wedding-public-guest-gallery-back-button">
                            <ChevronLeft className="mr-2 h-4 w-4" />
                            Back to wedding page
                          </Button>
                        </Link>
                      </div>
                    </section>
                  ) : (
                    <>
                      <h2 className="text-3xl font-semibold text-stone-900" data-testid="guest-gallery-upload-title">
                        Upload your photos from the wedding
                      </h2>
                      <p className="text-sm text-stone-600 mt-3" data-testid="guest-gallery-upload-subtitle">{coupleTitle}</p>
                      <p className="text-sm text-stone-600 mt-3" data-testid="guest-gallery-upload-helper-text">
                        Select one or multiple photos and upload them here. No login required.
                      </p>
                      <p className="text-xs text-stone-500 mt-2" data-testid="guest-gallery-upload-limit-note">
                        JPEG, PNG, WebP, or GIF · {maxPhotoLabel}
                      </p>

                      <form className="mt-6 space-y-4" onSubmit={handleUpload} data-testid="guest-gallery-upload-form">
                        <input
                          key={fileInputKey}
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={onFileChange}
                          className="block w-full rounded-sm border border-stone-300 p-3 text-sm"
                          data-testid="guest-gallery-upload-input"
                        />
                        <Button
                          type="submit"
                          disabled={uploading}
                          className="bg-[#5C0120] hover:bg-[#5C0120]/90"
                          data-testid="guest-gallery-upload-submit"
                        >
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                          Upload photos
                        </Button>
                      </form>

                      {uploadMessage && (
                        <p className="mt-4 text-sm text-stone-700" data-testid="guest-gallery-upload-message">{uploadMessage}</p>
                      )}

                      {uploadedPhotos.length > 0 && (
                        <div className="mt-6" data-testid="guest-gallery-upload-preview-section">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <h3 className="font-semibold text-stone-900" data-testid="guest-gallery-upload-preview-title">Recently uploaded</h3>
                            <p className="flex items-center gap-1 text-xs text-stone-500" data-testid="guest-gallery-upload-preview-hint">
                              <Eye className="h-3.5 w-3.5" />
                              Tap photo to view full screen
                            </p>
                          </div>

                          <GuestGalleryMasonryGrid
                            photos={uploadedPhotos}
                            onPhotoClick={setLightboxIndex}
                            testIdPrefix="guest-gallery-upload-preview"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <GuestGalleryLightbox
              isOpen={lightboxIndex !== null}
              photos={activeLightboxPhotos}
              currentIndex={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
              onNext={() => setLightboxIndex((prev) => (prev === null ? 0 : (prev + 1) % activeLightboxPhotos.length))}
              onPrev={() => setLightboxIndex((prev) => (prev === null ? 0 : (prev - 1 + activeLightboxPhotos.length) % activeLightboxPhotos.length))}
              testIdPrefix={isPublicGalleryMode ? 'wedding-public-guest-gallery-lightbox' : 'guest-gallery-upload-lightbox'}
            />

            <footer className="mt-8 text-center" data-testid="guest-gallery-upload-powered-by-footer">
              <Link
                to="/"
                className="text-xs text-stone-500 hover:text-[#5C0120] underline-offset-4 hover:underline"
                data-testid="guest-gallery-upload-powered-by-link"
              >
                Powered by Gibraltar Wedding Expo
              </Link>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
